import { endOfMinute, startOfMinute } from 'date-fns';

/**
 * Named calendar quick-periods offered by DateRangeSelectionComponent's
 * preset bar. Consumers may also resolve one programmatically via
 * quickPeriodRange() (e.g. a view seeding its default period), so the range
 * a name stands for is defined once, here.
 */
export const QUICK_PERIOD_VALUES = [
  '1Y',
  '6M',
  '3M',
  '1M',
  '2W',
  '1W',
  '3D',
  '24H',
  'today',
] as const;

export type QuickPeriodValue = (typeof QUICK_PERIOD_VALUES)[number];

export function isQuickPeriodValue(value: string): value is QuickPeriodValue {
  return (QUICK_PERIOD_VALUES as readonly string[]).includes(value);
}

/**
 * The date range a named preset stands for: calendar periods anchor to today
 * (local start-of-day -> end-of-day), '24H' alone is now-relative. Normalized
 * to the :00/:59 convention - seconds never ride into a range, since ranges
 * travel into the query-cache keys (the memcached layer in front of Solr).
 */
export function quickPeriodRange(period: QuickPeriodValue): {
  from: Date;
  to: Date;
} {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  // End of today
  const todayEnd = new Date(todayStart);
  todayEnd.setHours(23, 59, 59, 999);

  // Period-to-calendar logic
  const periodMap: Record<QuickPeriodValue, (start: Date) => Date> = {
    today: (start) => start,
    '24H': () => new Date(Date.now() - 24 * 60 * 60 * 1000),

    '3D': (start) => {
      let d = new Date(start);
      d.setDate(d.getDate() - 3);
      return d;
    },
    '1W': (start) => {
      let d = new Date(start);
      d.setDate(d.getDate() - 7);
      return d;
    },
    '2W': (start) => {
      let d = new Date(start);
      d.setDate(d.getDate() - 14);
      return d;
    },

    '1M': (start) => {
      let d = new Date(start);
      d.setMonth(d.getMonth() - 1);
      return d;
    },
    '3M': (start) => {
      let d = new Date(start);
      d.setMonth(d.getMonth() - 3);
      return d;
    },
    '6M': (start) => {
      let d = new Date(start);
      d.setMonth(d.getMonth() - 6);
      return d;
    },
    '1Y': (start) => {
      let d = new Date(start);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    },
  };

  const from = periodMap[period](todayStart);
  const to = period === '24H' ? new Date() : todayEnd;

  return { from: startOfMinute(from), to: endOfMinute(to) };
}
